/******************************************************************************
 * AdminCustomerDedupTab : écran super-admin de nettoyage des doublons clients.
 * Endpoints :
 *   GET  admin/customer_duplicates -> rapport, lecture seule (core, module app)
 *   POST admin/customers/merge     -> fusion, l'endpoint qui existait déjà
 *
 * La détection tourne côté serveur : le nom suit la stratégie des types de
 * machines, le numéro de TVA ajoute un lien fort. Un groupe marqué
 * `requiresReview` revient sans survivant proposé — plusieurs liens Odoo, des
 * numéros de TVA différents, ou une similarité trop fragile pour qu'un outil
 * tranche à la place d'un humain.
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import { t } from "i18next";
import { Button, Typography } from "@material-tailwind/react";
/***/

/* Components */
import Loading from "../../../../../../components/loading/Loading";
import { Table } from "../../../../../../components/table/Table";
import CustomerDuplicateGroupRow from "./CustomerDuplicateGroupRow";
/***/

/* Services */
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
/***/

/**
* AdminCustomerDedupTab : onglet de déduplication des clients
*/
export default function AdminCustomerDedupTab() {
  const [report, setReport] = useState<EasyTech.CustomerDuplicateReport | null>(null);
  const [loadError, setLoadError] = useState(false);

  /**
  * loadReport : charger le rapport de doublons
  */
  const loadReport = useCallback(() => {
    // setReport(null) démonte chaque CustomerDuplicateGroupRow avant que le nouveau
    // rapport ne s'affiche. La ligne initialise son survivant une seule fois par
    // useState et ne le resynchronise jamais contre group.members : ce démontage est
    // ce qui empêche un survivant hérité d'une forme de groupe précédente d'être
    // proposé à la fusion.
    setReport(null);
    setLoadError(false);
    api.get("admin/customer_duplicates")
      .then((ret) => setReport(ret as EasyTech.CustomerDuplicateReport))
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.customerDedup.errors.load") });
        setLoadError(true);
      });
  }, []);
  /***/

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const TABLE_HEAD = [
    { label: t("admin:tools.customerDedup.table.names"), orderKey: null },
    { label: t("admin:tools.customerDedup.table.members"), orderKey: null },
    { label: t("admin:tools.customerDedup.table.history"), orderKey: null },
    { label: t("admin:tools.customerDedup.table.confidence"), orderKey: null },
    { label: t("admin:tools.customerDedup.table.actions"), orderKey: null }
  ];

  return (
    <div className="my-4">
      <div className="flex items-center justify-end gap-4 mb-4">
        <Button size="sm" variant="outlined" data-testid="dedup-reload" onClick={loadReport}>
          {t("admin:tools.customerDedup.reload")}
        </Button>
      </div>

      <Typography variant="small" className="text-gray-600 mb-4">
        {t("admin:tools.customerDedup.notice")}
      </Typography>

      {loadError ? (
        <div className="flex flex-col items-start gap-2" data-testid="dedup-error">
          <Typography variant="small" color="red">
            {t("admin:tools.customerDedup.errors.load")}
          </Typography>
          <Button size="sm" variant="outlined" data-testid="dedup-retry" onClick={loadReport}>
            {t("common:retry")}
          </Button>
        </div>
      ) : report ? (
        <>
          <div className="flex flex-wrap gap-6 mb-4" data-testid="dedup-summary">
            <Typography variant="small">
              {t("admin:tools.customerDedup.summary.groups", { count: report.groupCount })}
            </Typography>
            <Typography variant="small">
              {t("admin:tools.customerDedup.summary.rows", { count: report.duplicateRowCount })}
            </Typography>
            <Typography variant="small">
              {t("admin:tools.customerDedup.summary.review", { count: report.reviewRequiredCount })}
            </Typography>
          </div>

          <Table tableHead={TABLE_HEAD}
            emptyTable={report.groups.length === 0 ? t("admin:tools.customerDedup.empty") : false}>
            {report.groups.map((group) => (
              <CustomerDuplicateGroupRow
                key={group.groupId + ":" + group.members.map((m) => m.id).join(",")}
                group={group}
                onMerged={loadReport} />
            ))}
          </Table>
        </>
      ) : <Loading />}
    </div>
  );
}
/***/
